import React, { Fragment, useEffect, useState } from 'react';
import {
  Container,
  Text,
  useSteps,
  Box,
  FormControl,
  FormLabel,
  Input,
  Flex,
  useToast,
  Radio,
  RadioGroup,
  Stack,
  Heading,
} from '@chakra-ui/react';
import CampaignStepper from './components/CampaignStepper';
import {
  campaignSteps,
  prepareDraftCampaignData,
  prepareTargetedAudienceData,
} from '../../_helpers/data_helper';
import AudienceSelection from './components/AudienceSelection';
import PrimaryButton from '../../components/themeComponents/PrimaryButton';
import { useQuery } from 'react-query';
import { connect } from 'react-redux';
import {
  draftCampaign,
  getCampaign,
  publishABCampaign,
  publishCampaign,
} from '../../Services/api';
import { OvalSpinner } from '../../components/Loaders';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { broadcastCampaign as initialValues } from '../../_helpers/formValues';
import _ from 'lodash';
import OutlinedButton from '../../components/themeComponents/OutlinedButton';
import TemplateSelection from './components/TemplateSelection';
import ScheduleCampaign from './components/ScheduleCampaign';
import TemplateSelectionAB from './components/TemplateSelectionAB';
import { sendAbTest, saveMultipleSchedule } from '../../Services/api';
import Swal from 'sweetalert2';

function CreateCampaign({ org, reach }) {
  const [showSliders, setShowSlider] = useState(false);
  const [totalReach, setTotalReach] = useState(0);
  const [targetRatios, setTargetRatios] = useState({ '0': 100 });
  const [isValidRatios, setIsValidRatios] = useState(true);
  const [audienceType, setAudienceType] = useState('segment');

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: campaignSteps.length,
  });

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      selectedChannel: '', // important: do NOT hardcode 'email' here
    },
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      handleSubmit(values, actions, false);
    },
  });

  const campaignQuery = useQuery(
    ['campaign', id],
    async () => {
      if (!id) return null;
      const res = await getCampaign(org.token, id);
      const data = prepareDraftCampaignData(res.data);
      formik.setValues(data);
      return data;
    },
    {
      enabled: !!id,
      cacheTime: 0,
      retry: 0,
      onError: (err) =>
        toast({
          title: 'Failed to load campaign',
          description: err?.message,
          status: 'error',
        }),
    }
  );

  useEffect(() => {
    if (location.state?.data?.campaignData) {
      formik.setValues(location.state.data.campaignData);
    } else if (location.state) {
      formik.setValues({ ...initialValues, ...location.state });
    }
  }, [location.state]);

  const saveAbTest = async (values, action) => {
    try {
      values.broadcastType = 'a_b_test_v2';
      values.broadcast = 'a_b_test_v2';
      values['uuid'] = window.location.pathname.split('/')[2];

      let campaignData = { ...values };
      let targetedAudience = {};

      if (
        !_.isEmpty(campaignData.targetedAudience.includes.CSV) ||
        !_.isEmpty(campaignData.targetedAudience.includes.segment)
      ) {
        targetedAudience = prepareTargetedAudienceData(
          campaignData.targetedAudience,
          values.broadcastType,
          values.selectedChannel
        );
      }
      campaignData.targetedAudience = targetedAudience;

      const abTestResp = await sendAbTest(org.token, campaignData);

      if (abTestResp.data && abTestResp.status === '200') {
        let publishResponse = await publishABCampaign(
          abTestResp.data,
          campaignData.scheduleData,
          campaignData.scheduleStatus,
          abTestResp.data[0].orgId,
          values?.uuid
        );

        action.setSubmitting(false);
        toast({
          title: 'AB Test campaign created & published',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        setIsSubmitting(false);
        navigate('/campaign/list');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message || err?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const saveMultiSchedule = async (values, action) => {
    try {
      values.broadcastType = 'BATCH_WISE_CONTROL_GROUP';
      values.broadcast = 'BATCH_WISE_CONTROL_GROUP';
      values['uuid'] = window.location.pathname.split('/')[2];

      let campaignData = { ...values };
      let targetedAudience = {};

      if (
        !_.isEmpty(campaignData.targetedAudience.includes.CSV) ||
        !_.isEmpty(campaignData.targetedAudience.includes.segment)
      ) {
        targetedAudience = prepareTargetedAudienceData(
          campaignData.targetedAudience,
          values.broadcastType,
          values.selectedChannel
        );
      }
      campaignData.targetedAudience = targetedAudience;

      const multiScheduleGroup = await saveMultipleSchedule(org.token, campaignData);
      let hasError = false;

      if (multiScheduleGroup.data && multiScheduleGroup.status === '200') {
        multiScheduleGroup?.data.forEach((datum) => {
          if (datum.result === 'failure') {
            Swal.fire({
              title: 'Error',
              text: datum.message,
              icon: 'error',
              confirmButtonText: 'Okay',
            });
            hasError = true;
            setIsSubmitting(false);
            return;
          }
        });

        if (!hasError) {
          action.setSubmitting(false);
          toast({
            title: 'Multi-schedule campaign saved & published',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
          setIsSubmitting(false);
          navigate('/campaign/list');
        }
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message || err?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const saveAndPublishCampaign = async (values, action, isDraft = false) => {
    try {
      values['uuid'] = window.location.pathname.split('/')[2];

      if (values.campaignType === 'multiple_schedule_v2' && !isDraft) {
        saveMultiSchedule(values, action);
        return;
      }

      if (values.campaignType === 'control_group_v2') {
        values['broadcastType'] = 'CONTROL_GROUP';
        values['campaignType'] = 'CONTROL_GROUP';
        values['broadcast'] = 'CONTROL_GROUP';
      }

      if (values.scheduleData?.scheduleStatus === 'SCHEDULED') {
        values['scheduleAt'] = values?.scheduleData?.scheduleAt;
        values['scheduleStatus'] = values?.scheduleData?.scheduleStatus;
      }

      let campaignData = { ...values };
      let targetedAudience = {};

      if (
        !_.isEmpty(campaignData.targetedAudience.includes.CSV) ||
        !_.isEmpty(campaignData.targetedAudience.includes.segment)
      ) {
        targetedAudience = prepareTargetedAudienceData(
          campaignData.targetedAudience,
          values.broadcastType || values.campaignType,
          values.selectedChannel
        );
      }
      campaignData.targetedAudience = targetedAudience;

      const draftResponse = await draftCampaign(campaignData);

      if (isDraft) {
        setIsSubmitting(false);
        navigate('/campaign/list');
        return;
      }

      if (draftResponse.data && draftResponse.data.result === 'success' && !isDraft) {
        let publishResponse = await publishCampaign(
          org.token,
          draftResponse.data.id,
          campaignData.scheduleData
        );

        action.setSubmitting(false);
        toast({
          title: `${publishResponse?.data?.result || publishResponse.data.status}. Campaign published.`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        setIsSubmitting(false);
        navigate('/campaign/list');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.message || err?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values, action, isDraft = false) => {
    setIsSubmitting(true);
    try {
      await saveAndPublishCampaign(values, action, isDraft);
    } catch (err) {
      console.error('Submit error:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      maxW="1230px"
      p={4}
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
    >
      <CampaignStepper steps={campaignSteps} activeStep={activeStep} />

      {activeStep === 0 && (
        <Fragment>
          <Box
            maxW="930px"
            w="100%"
            bg="white"
            m="0 auto"
            mt={5}
            p={6}
            borderRadius="md"
            shadow="sm"
          >
            <Heading size="md" mb={6} color="heading">
              Campaign Information
            </Heading>

            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
              <FormControl isRequired>
                <FormLabel>Campaign Name</FormLabel>
                <Input
                  {...formik.getFieldProps('campaignName')}
                  placeholder="Enter campaign name"
                />
              </FormControl>

              
            </Flex>
          </Box>

          <Box
            maxW="930px"
            w="100%"
            bg="white"
            m="20px auto"
            p={6}
            borderRadius="md"
            shadow="sm"
            overflowY="auto"
            maxH="60vh"
          >
            <Heading size="md" mb={6} color="heading">
              Audience Selection
            </Heading>

            {formik.values.selectedChannel ? (
              <AudienceSelection
                setTotalReach={setTotalReach}
                totalReach={totalReach}
                audienceType={audienceType}
                setAudienceType={setAudienceType}
                selectedChannel={formik.values.selectedChannel}
                showSliders={showSliders}
                setShowSlider={setShowSlider}
                orgToken={org.token}
                orgId={org.id}
                reach={reach}
                formik={formik}
              />
            ) : (
              <Text color="gray.500" fontStyle="italic" textAlign="center" py={10}>
                Please select a channel above to configure audience
              </Text>
            )}
          </Box>
        </Fragment>
      )}

      {activeStep === 1 && formik.values.campaignType !== 'a_b_test_v2' && (
        <TemplateSelection
          isValidRatios={isValidRatios}
          setIsValidRatios={setIsValidRatios}
          selectedChannel={formik.values.selectedChannel}
          orgToken={org.token}
          targetRatios={targetRatios}
          setTargetRatios={setTargetRatios}
          formik={formik}
        />
      )}

      {activeStep === 1 && formik.values.campaignType === 'a_b_test_v2' && (
        <TemplateSelectionAB
          selectedChannel={formik.values.selectedChannel}
          orgToken={org.token}
          reach={reach}
          formik={formik}
        />
      )}

      {activeStep === 2 && (
        <ScheduleCampaign
          selectedChannel={formik.values.selectedChannel}
          formik={formik}
          errors={[]}
        />
      )}

      {/* Fixed bottom bar */}
      <Flex
        h="5rem"
        bg="white"
        w="calc(100% - 15rem)"
        pos="fixed"
        right={0}
        bottom={0}
        justifyContent="flex-end"
        alignItems="center"
        px={6}
        borderTop="1px solid"
        borderColor="gray.200"
        zIndex={10}
      >
        <OutlinedButton
          label="Back"
          mr={3}
          onClick={() => {
            if (activeStep === 0) {
              navigate('/campaign/list');
            } else {
              setActiveStep(activeStep - 1);
            }
          }}
        />

        <OutlinedButton
          label="Save Draft"
          mr={3}
          onClick={() => handleSubmit(formik.values, formik, true)}
          isLoading={isSubmitting}
        />

        {activeStep === 2 ? (
          <PrimaryButton
            label="Publish"
            isDisabled={
              isSubmitting ||
              !formik.values.selectedChannel ||
              (formik.values.scheduleData?.scheduleStatus === 'SCHEDULED' &&
                !formik.values.scheduleData?.scheduleAt) ||
              (formik.values.campaignType === 'a_b_test_v2' &&
                _.isEmpty(formik?.values?.scheduleData?.targetRatios))
            }
            onClick={formik.handleSubmit}
            isLoading={isSubmitting}
          />
        ) : (
          <PrimaryButton
            label="Next"
            isDisabled={
              !formik.values.selectedChannel ||
              (activeStep === 0 && totalReach === 0) ||
              (activeStep === 1 &&
                formik.values.selectedChannel !== 'email' &&
                _.sum(Object.values(targetRatios)) !== 100) ||
              (activeStep === 1 &&
                formik.values.selectedChannel === 'email' &&
                !formik.values.selectedSmtpUuid)
            }
            onClick={() => setActiveStep(activeStep + 1)}
          />
        )}
      </Flex>
    </Container>
  );
}

const mapStateToProps = (state) => ({
  org: state.auth.selectedOrg,
  reach: state.campaign.reach,
});

export default connect(mapStateToProps)(CreateCampaign);